import type { PayoutDisplayState } from '@/components/payouts/payoutDisplayState';
import {
  CHARGED_TRANSFERS_INCOMPLETE,
  EXPIRED,
  PAYMENT_DECLINED,
  resolvePayoutDisplayState,
} from '@/components/payouts/payoutDisplayState';
import type { BadgeProps } from '@chakra-ui/react';
import { Badge } from '@chakra-ui/react';
import type { PayoutBatchClosedReasonValue, PayoutBatchStatus } from '@fluenceur/types';

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Circle,
  Clock,
  CreditCardSlash,
  Hourglass,
  TimerOff,
  WarningCircle,
  WarningTriangle,
  XmarkCircle,
} from 'iconoir-react';

interface PayoutBatchStatusBadgeProps extends BadgeProps {
  status: PayoutBatchStatus;
  /** Instant the brand's payment was accepted, null or absent while it was not. */
  fundedAt?: string | null;
  /** Instant of the last refused payment attempt, null or absent when none was refused. */
  lastPaymentFailedAt?: string | null;
  /** Why an unpaid payout was closed, null or absent while it is open or was paid. */
  closedReason?: PayoutBatchClosedReasonValue | null;
}

// Define color schemes for each display state
const statuscolorPalettes: Record<PayoutDisplayState, string> = {
  draft: 'gray',
  pending: 'yellow',
  processing: 'blue',
  completed: 'green',
  partially_completed: 'orange',
  failed: 'red',
  cancelled: 'gray',
  [EXPIRED]: 'gray',
  [PAYMENT_DECLINED]: 'red',
  [CHARGED_TRANSFERS_INCOMPLETE]: 'orange',
};

// Map each display state to a Lucide icon
const statusIcons: Record<PayoutDisplayState, React.ElementType> = {
  draft: Circle,
  pending: Clock,
  processing: Hourglass,
  completed: Check,
  partially_completed: Check,
  failed: WarningCircle,
  cancelled: XmarkCircle,
  [EXPIRED]: TimerOff,
  // No tick on either: the money did not move the way the brand expects.
  [PAYMENT_DECLINED]: CreditCardSlash,
  [CHARGED_TRANSFERS_INCOMPLETE]: WarningTriangle,
};

// Map display state to translation key
const statusTranslationKeys: Record<PayoutDisplayState, string> = {
  draft: 'payouts.status.draft',
  pending: 'payouts.status.pending',
  processing: 'payouts.status.processing',
  completed: 'payouts.status.completed',
  partially_completed: 'payouts.status.partiallyCompleted',
  failed: 'payouts.status.failed',
  cancelled: 'payouts.status.cancelled',
  [EXPIRED]: 'payouts.status.expired',
  [PAYMENT_DECLINED]: 'payouts.status.paymentDeclined',
  [CHARGED_TRANSFERS_INCOMPLETE]: 'payouts.status.chargedTransfersIncomplete',
};

export const PayoutBatchStatusBadge: React.FC<PayoutBatchStatusBadgeProps> = ({
  status,
  fundedAt = null,
  lastPaymentFailedAt = null,
  closedReason = null,
  ...rest
}) => {
  const { t } = useTranslation();
  const displayState = resolvePayoutDisplayState({
    status,
    fundedAt,
    lastPaymentFailedAt,
    closedReason,
  });
  const colorPalette = statuscolorPalettes[displayState] || 'gray';
  const label = t(statusTranslationKeys[displayState] || 'payouts.status.pending');
  const IconComponent = statusIcons[displayState];

  return (
    <Badge colorPalette={colorPalette} gapX={0.5} {...rest}>
      {IconComponent && <IconComponent width={12} height={12} />}
      {label}
    </Badge>
  );
};

export default PayoutBatchStatusBadge;
